import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type {
  AxonFindingView,
  PlatformHealthAxonFindingsResponse,
  PlatformHealthCriticalItem,
  PlatformHealthCriticalResponse,
} from '@/pages/platform-health/types';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  CircleSlash,
  ExternalLink,
  FileWarning,
  RefreshCw,
  Search,
  ShieldAlert,
  Wrench,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const REFRESH_INTERVAL_MS = 60 * 1000;

function formatSince(iso: string | null): string {
  if (!iso) return 'time unknown';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'time unknown';
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

interface FindingFilters {
  search: string;
  lifecycle: 'active' | 'recovered' | 'retired' | 'all';
  subsystem: string;
  severity: string;
  impact: string;
  policy: string;
  criticalOnly: boolean;
}

const DEFAULT_FINDING_FILTERS: FindingFilters = {
  search: '',
  lifecycle: 'active',
  subsystem: 'all',
  severity: 'all',
  impact: 'all',
  policy: 'all',
  criticalOnly: false,
};

function filterAxonFindings(
  findings: AxonFindingView[],
  filters: FindingFilters
): AxonFindingView[] {
  const search = filters.search.trim().toLowerCase();
  return findings.filter((finding) => {
    if (filters.lifecycle !== 'all' && finding.lifecycle !== filters.lifecycle) return false;
    if (filters.subsystem !== 'all' && finding.subsystemId !== filters.subsystem) return false;
    if (filters.severity !== 'all' && finding.severity !== filters.severity) return false;
    if (filters.impact !== 'all' && finding.impact !== filters.impact) return false;
    if (filters.policy !== 'all' && finding.policy !== filters.policy) return false;
    if (filters.criticalOnly && !finding.critical) return false;
    if (!search) return true;
    return [
      finding.id,
      finding.summary,
      finding.subsystemName,
      finding.checkName,
      finding.ruleId,
      finding.owner.displayName,
      finding.subject?.id,
    ].some((value) => value?.toLowerCase().includes(search));
  });
}

function Badge({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'red' | 'amber' | 'green';
}) {
  const colors = {
    neutral: 'bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-gray-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
    green: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200',
  };
  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        colors[tone]
      )}
    >
      {children}
    </span>
  );
}

function FindingRow({ finding }: { finding: AxonFindingView }) {
  const Icon =
    finding.kind === 'problem' ? FileWarning : finding.critical ? ShieldAlert : AlertTriangle;
  return (
    <details
      data-testid="axon-finding-row"
      data-lifecycle={finding.lifecycle}
      className="group border-b border-gray-200 last:border-b-0 dark:border-zinc-700"
    >
      <summary className="flex cursor-pointer list-none items-start gap-3 px-4 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500">
        <Icon
          className={cn(
            'mt-0.5 h-5 w-5 shrink-0',
            finding.critical
              ? 'text-red-600'
              : finding.lifecycle === 'recovered'
                ? 'text-green-600'
                : 'text-amber-600'
          )}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-950 dark:text-white">
              {finding.summary}
            </span>
            {finding.critical ? <Badge tone="red">critical</Badge> : null}
            <Badge tone={finding.lifecycle === 'recovered' ? 'green' : 'neutral'}>
              {finding.lifecycle}
            </Badge>
            {finding.policy !== 'active' ? <Badge tone="amber">{finding.policy}</Badge> : null}
            {finding.kind === 'problem' ? <Badge tone="amber">evidence-plane problem</Badge> : null}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {finding.subsystemName} · {finding.checkName} · {finding.ruleId}
          </p>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
            Owner: {finding.owner.displayName} · {finding.recurrenceCount} occurrence
            {finding.recurrenceCount === 1 ? '' : 's'} · opened {formatSince(finding.openedAt)}
          </p>
        </div>
        <ChevronRight
          className="mt-1 h-4 w-4 shrink-0 text-gray-400 transition-transform group-open:rotate-90"
          aria-hidden="true"
        />
      </summary>
      <div className="border-t border-gray-100 bg-gray-50/70 px-4 py-4 dark:border-zinc-700 dark:bg-zinc-900/40">
        <p className="text-sm text-gray-700 dark:text-gray-200">{finding.explanation}</p>
        <dl className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
          <div>
            <dt className="text-gray-500">Severity</dt>
            <dd className="mt-0.5 font-medium capitalize">{finding.severity}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Impact</dt>
            <dd className="mt-0.5 font-medium">{finding.impact.replace(/_/g, ' ')}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Policy</dt>
            <dd className="mt-0.5 font-medium capitalize">{finding.policy}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Subject</dt>
            <dd className="mt-0.5 font-medium">
              {finding.subject ? `${finding.subject.kind}:${finding.subject.id}` : 'Evidence plane'}
            </dd>
          </div>
        </dl>
        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          <a
            href={finding.workbenchLink}
            className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            Owning workbench <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
          <a
            href={`/admin/platform-health/test-runs?finding=${encodeURIComponent(
              finding.id
            )}${finding.subsystemId ? `&subsystem=${encodeURIComponent(finding.subsystemId)}` : ''}`}
            className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            Related runs <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
          {finding.evidence.map((evidence) =>
            evidence.uri ? (
              <a
                key={evidence.evidenceReferenceId}
                href={evidence.uri}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
              >
                {evidence.kind} evidence <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            ) : (
              <span
                key={evidence.evidenceReferenceId}
                className="text-amber-700 dark:text-amber-300"
              >
                Missing evidence: {evidence.evidenceReferenceId}
              </span>
            )
          )}
        </div>
        {finding.retirement ? (
          <p className="mt-4 text-xs text-gray-600 dark:text-gray-300">
            Retired {formatSince(finding.retirement.retiredAt)} by {finding.retirement.retiredBy} —{' '}
            {finding.retirement.reason}
          </p>
        ) : null}
        {finding.waiver || finding.adjudication ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {finding.adjudication ? (
              <section className="rounded border border-gray-200 bg-white p-3 text-xs dark:border-zinc-700 dark:bg-zinc-800">
                <h3 className="font-semibold">Adjudication · {finding.adjudication.decision}</h3>
                <p className="mt-1 text-gray-600 dark:text-gray-300">
                  {finding.adjudication.rationale}
                </p>
              </section>
            ) : null}
            {finding.waiver ? (
              <section className="rounded border border-amber-300 bg-amber-50 p-3 text-xs dark:border-amber-800 dark:bg-amber-950/30">
                <h3 className="font-semibold">
                  Waiver · expires {formatSince(finding.waiver.expiresAt)}
                </h3>
                <p className="mt-1">{finding.waiver.rationale}</p>
              </section>
            ) : null}
          </div>
        ) : null}
        {finding.actions.length > 0 ? (
          <section className="mt-4">
            <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-950 dark:text-white">
              <Wrench className="h-3.5 w-3.5" aria-hidden="true" /> Remediation actions
            </h3>
            <ul className="mt-2 space-y-1 text-xs">
              {finding.actions.map((action) => (
                <li
                  key={action.actionReferenceId}
                  className="flex flex-wrap items-center justify-between gap-2 rounded border border-gray-200 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-800"
                >
                  <span>
                    {action.system}:{action.externalId} ·{' '}
                    <strong>{action.status.replace(/_/g, ' ')}</strong>
                  </span>
                  {action.uri ? (
                    <a
                      href={action.uri}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                      Open action
                    </a>
                  ) : (
                    <span className="text-amber-700">Action link unavailable</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        {finding.recovery ? (
          <section className="mt-4 rounded border border-green-300 bg-green-50 p-3 text-xs text-green-900 dark:border-green-800 dark:bg-green-950/30 dark:text-green-100">
            <h3 className="font-semibold">Recovery proved by {finding.recovery.attestationId}</h3>
            <p className="mt-1">
              {finding.recovery.summary || 'Fresh owning revalidation passed.'}
            </p>
          </section>
        ) : null}
        {finding.history.length > 0 ? (
          <section className="mt-4">
            <h3 className="text-xs font-semibold text-gray-950 dark:text-white">
              Lifecycle history
            </h3>
            <ol className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
              {finding.history.map((event) => (
                <li key={event.eventId}>
                  #{event.sequence} {event.eventType.replace(/_/g, ' ')} ·{' '}
                  {formatSince(event.occurredAt)}
                </li>
              ))}
            </ol>
          </section>
        ) : null}
        <p className="mt-4 break-all text-[10px] text-gray-500 dark:text-gray-400">
          Inputs: {finding.inputRecordIds.join(', ') || 'none'}
        </p>
      </div>
    </details>
  );
}

function SelectFilter({
  label,
  value,
  values,
  onChange,
}: {
  label: string;
  value: string;
  values: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="text-xs text-gray-600 dark:text-gray-300">
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white"
      >
        {values.map((item) => (
          <option key={item} value={item}>
            {item === 'all' ? `All ${label.toLowerCase()}` : item.replace(/_/g, ' ')}
          </option>
        ))}
      </select>
    </label>
  );
}

export function AxonFindingsContent({ payload }: { payload: PlatformHealthAxonFindingsResponse }) {
  const [searchParams] = useSearchParams();
  const linkedFinding = searchParams.get('finding');
  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FINDING_FILTERS,
    search: linkedFinding ?? '',
    lifecycle: linkedFinding ? ('all' as const) : DEFAULT_FINDING_FILTERS.lifecycle,
  }));
  const visible = useMemo(
    () => filterAxonFindings(payload.findings, filters),
    [payload.findings, filters]
  );
  const subsystems = [
    'all',
    ...new Set(
      payload.findings
        .map((finding) => finding.subsystemId)
        .filter((value): value is string => !!value)
    ),
  ];
  const severities = ['all', ...new Set(payload.findings.map((finding) => finding.severity))];
  const impacts = ['all', ...new Set(payload.findings.map((finding) => finding.impact))];
  const policies = ['all', ...new Set(payload.findings.map((finding) => finding.policy))];

  return (
    <div className="space-y-4">
      {!payload.available ? (
        <div
          data-testid="axon-findings-unavailable"
          className="rounded-lg border border-gray-300 bg-gray-50 p-4 text-sm text-gray-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-gray-100"
        >
          <strong>AXON findings are unavailable.</strong> The reducer snapshot or matching registry
          cannot be read; an empty list would not mean all-clear.
        </div>
      ) : null}
      {payload.partial ? (
        <div
          data-testid="axon-findings-partial"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <strong>Partial findings context.</strong>
          <ul className="mt-2 list-disc pl-5 text-xs">
            {payload.gaps.map((gap, index) => (
              <li key={`${gap.code}:${index}`}>{gap.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['Active', payload.counts.active],
          ['Critical', payload.counts.critical],
          ['Recovered history', payload.counts.recovered],
          ['Evidence problems', payload.counts.problems],
        ].map(([label, value]) => (
          <div
            key={String(label)}
            className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800"
          >
            <dt className="text-xs text-gray-500">{label}</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums text-gray-950 dark:text-white">
              {value}
            </dd>
          </div>
        ))}
      </dl>
      <section
        aria-label="Finding filters"
        className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800"
      >
        <div className="flex flex-wrap gap-2">
          <label className="relative min-w-52 flex-1">
            <span className="sr-only">Search findings</span>
            <Search
              className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400"
              aria-hidden="true"
            />
            <input
              aria-label="Search findings"
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({ ...current, search: event.target.value }))
              }
              placeholder="Search findings and recovered history"
              className="h-9 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
            />
          </label>
          <SelectFilter
            label="Lifecycle"
            value={filters.lifecycle}
            values={['active', 'recovered', 'retired', 'all']}
            onChange={(value) =>
              setFilters((current) => ({
                ...current,
                lifecycle: value as FindingFilters['lifecycle'],
              }))
            }
          />
          <SelectFilter
            label="Subsystems"
            value={filters.subsystem}
            values={subsystems}
            onChange={(value) => setFilters((current) => ({ ...current, subsystem: value }))}
          />
          <SelectFilter
            label="Severities"
            value={filters.severity}
            values={severities}
            onChange={(value) => setFilters((current) => ({ ...current, severity: value }))}
          />
          <SelectFilter
            label="Impacts"
            value={filters.impact}
            values={impacts}
            onChange={(value) => setFilters((current) => ({ ...current, impact: value }))}
          />
          <SelectFilter
            label="Policies"
            value={filters.policy}
            values={policies}
            onChange={(value) => setFilters((current) => ({ ...current, policy: value }))}
          />
          <label className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 px-3 text-xs font-medium text-gray-700 dark:border-zinc-600 dark:text-gray-200">
            <input
              type="checkbox"
              checked={filters.criticalOnly}
              onChange={(event) =>
                setFilters((current) => ({ ...current, criticalOnly: event.target.checked }))
              }
            />{' '}
            Critical only
          </label>
        </div>
      </section>
      {payload.available && visible.length === 0 ? (
        <div
          data-testid="axon-findings-empty"
          className="rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-zinc-700 dark:bg-zinc-800"
        >
          <CircleSlash className="mx-auto h-8 w-8 text-gray-400" aria-hidden="true" />
          <p className="mt-4 text-sm font-medium text-gray-950 dark:text-white">
            No findings match these filters
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Recovered findings remain available through the lifecycle filter.
          </p>
        </div>
      ) : null}
      {visible.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
          {visible.map((finding) => (
            <FindingRow key={finding.id} finding={finding} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function CompatibilityCritical({ payload }: { payload: PlatformHealthCriticalResponse }) {
  return (
    <div className="space-y-4">
      {payload.degraded.length > 0 ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          Couldn&apos;t read {payload.degraded.join(', ')} — compatibility data is incomplete.
        </div>
      ) : null}
      {payload.allClear ? (
        <div className="rounded-lg border border-green-300 bg-green-50 p-12 text-center dark:border-green-800 dark:bg-green-950/40">
          <CheckCircle2 className="mx-auto h-8 w-8 text-green-600" aria-hidden="true" />
          <p className="mt-4 text-sm font-medium text-green-900 dark:text-green-100">
            Compatibility sources report all clear
          </p>
        </div>
      ) : null}
      {payload.items.length > 0 ? (
        <ul className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
          {payload.items.map((item: PlatformHealthCriticalItem) => (
            <li
              key={item.id}
              className="flex items-start gap-3 border-b border-gray-200 p-4 last:border-b-0 dark:border-zinc-700"
            >
              <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-gray-950 dark:text-white">{item.title}</p>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{item.detail}</p>
                {item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-xs font-medium text-blue-600 hover:underline"
                  >
                    Open source
                  </a>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function PlatformHealthCriticalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const compatibility = searchParams.get('readModel') === 'compatibility';
  const axonQuery = useQuery({
    queryKey: ['platform-health', 'axon', 'findings'],
    queryFn: () => api.getPlatformHealthAxonFindings(),
    refetchInterval: REFRESH_INTERVAL_MS,
    enabled: !compatibility,
  });
  const compatibilityQuery = useQuery({
    queryKey: ['platform-health', 'compatibility', 'critical'],
    queryFn: () => api.getPlatformHealthCritical(),
    refetchInterval: REFRESH_INTERVAL_MS,
    enabled: compatibility,
  });
  const query = compatibility ? compatibilityQuery : axonQuery;
  const payload = compatibility ? compatibilityQuery.data?.data : axonQuery.data?.data;

  function switchModel() {
    const next = new URLSearchParams(searchParams);
    if (compatibility) next.delete('readModel');
    else next.set('readModel', 'compatibility');
    setSearchParams(next);
  }

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Findings"
        subtitle="Active issues, evidence gaps, ownership, remediation, recurrence, and recovery history"
      />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {payload?.generatedAt
            ? `Updated ${formatSince(payload.generatedAt)}`
            : 'Loading findings…'}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={switchModel}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-gray-200 dark:hover:bg-zinc-700"
          >
            {compatibility ? 'Return to AXON' : 'Compatibility view'}
          </button>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-gray-200 dark:hover:bg-zinc-700"
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5', query.isFetching && 'animate-spin')}
              aria-hidden="true"
            />{' '}
            Refresh
          </button>
        </div>
      </div>
      {compatibility ? (
        <div
          data-testid="compatibility-read-model"
          className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <strong>Compatibility read model.</strong> This legacy “what is critical” aggregation is
          retained for rollback and parity inspection; it is not the canonical AXON finding
          lifecycle.
        </div>
      ) : null}
      {query.isError ? (
        <div
          data-testid="platform-health-findings-error"
          className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          Couldn&apos;t load findings: {(query.error as Error)?.message || 'unknown error'}
        </div>
      ) : null}
      {query.isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center text-sm text-gray-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-gray-400">
          Loading findings…
        </div>
      ) : null}
      {!compatibility && axonQuery.data?.data ? (
        <AxonFindingsContent payload={axonQuery.data.data} />
      ) : null}
      {compatibility && compatibilityQuery.data?.data ? (
        <CompatibilityCritical payload={compatibilityQuery.data.data} />
      ) : null}
    </div>
  );
}
